import { useCallback, useEffect, useMemo, useRef } from 'react';
import date from 'date-and-time';
import { PostPlatformSettingsMap } from 'fym-shared/src/music24/postPlatformSettings';
import {
  useGetPromoPostsQuery,
  useMovePromoPostMutation,
  usePatchPromoPostMutation,
  useSchedulePromoPostMutation,
} from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useQueryParam } from '../../../../utils/hooks/useQueryParam';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { ComposerDraft } from './useComposerDraft';

interface UseComposerEditPostProps {
  accountId: number;
  draft: ComposerDraft;
  /** The catalog songs: the release of an edited post is the song of its SmartLink. */
  items?: CatalogItem[];
}

/**
 * The catalog song of an edited post: the song whose SmartLink the post carries. Undefined for a
 * post with no SmartLink, or a SmartLink of no song. Pure.
 */
export function releaseOfEditedPost(
  smartLinkId: number | null | undefined,
  items: readonly CatalogItem[] | undefined
): string | undefined {
  if (smartLinkId === null || smartLinkId === undefined) {
    return undefined;
  }
  return items?.find((item) => item.smartLinkId === smartLinkId || item.smartLinkIds?.includes(smartLinkId) === true)
    ?.uuid;
}

/**
 * "Edit in Composer" of the Calendar (`?postUuid=`): the Composer opens on one existing post. It
 * fills the caption, the channel, the format and the time once, and a save changes that post:
 * the caption (PATCH), the time (a move keeps the state; a failed post gets a new time through
 * PATCH), and "Schedule" arms a draft. The media of the post stays.
 */
export function useComposerEditPost({ accountId, draft, items }: UseComposerEditPostProps) {
  const postUuid = useQueryParam<null>('postUuid', null, null);
  const { data, isLoading } = useGetPromoPostsQuery({ accountId }, { skip: postUuid === null });
  const post = useMemo(() => data?.posts.find((one) => one.uuid === postUuid), [data, postUuid]);
  const [patchPost, patchState] = usePatchPromoPostMutation();
  const [movePost, moveState] = useMovePromoPostMutation();
  const [schedulePost, scheduleState] = useSchedulePromoPostMutation();
  const isFilled = useRef(false);
  const isReleaseFilled = useRef(false);
  const { setMaster, selectOnly, changeFormat, setTiming, setPickedAt, usable, itemUuid, setItemUuid } = draft;

  // The release of the post, once: the media picker then opens on its song (QA finding B16).
  useEffect(() => {
    if (isReleaseFilled.current || post === undefined || items === undefined) {
      return;
    }
    isReleaseFilled.current = true;
    const release = releaseOfEditedPost(post.smartLinkId, items);
    if (itemUuid === undefined && release !== undefined) {
      setItemUuid(release);
    }
  }, [itemUuid, items, post, setItemUuid]);

  useEffect(() => {
    if (isFilled.current || post === undefined || usable.length === 0) {
      return;
    }
    isFilled.current = true;
    setMaster(post.caption);
    selectOnly(post.platform);
    changeFormat(post.format);
    setTiming('pick');
    setPickedAt(date.format(new Date(post.scheduledAt), 'YYYY-MM-DDTHH:mm'));
  }, [changeFormat, post, selectOnly, setMaster, setPickedAt, setTiming, usable.length]);

  /** Saves the changes to the post. `asDraft`: a draft stays a draft. */
  const save = useCallback(
    async (asDraft: boolean, platformSettings?: PostPlatformSettingsMap): Promise<boolean> => {
      if (post === undefined) {
        return false;
      }
      // The caption of the channel of the post: a caption tailored for that channel wins.
      const caption = draft.captionFor(post.platform);
      if (caption !== post.caption) {
        const result = await patchPost({ accountId, postUuid: post.uuid, caption });
        if (!isSuccessfulResult(result)) {
          return false;
        }
      }
      // The settings of the platform of the post (TikTok), saved before it is armed.
      if (
        platformSettings !== undefined &&
        platformSettings[post.platform as keyof PostPlatformSettingsMap] !== undefined
      ) {
        const result = await patchPost({ accountId, postUuid: post.uuid, platformSettings });
        if (!isSuccessfulResult(result)) {
          return false;
        }
      }
      const picked = draft.pickedAt === '' ? null : new Date(draft.pickedAt);
      if (picked !== null && Math.abs(picked.getTime() - new Date(post.scheduledAt).getTime()) >= 60_000) {
        const result =
          post.status === 'failed'
            ? await patchPost({ accountId, postUuid: post.uuid, scheduledAt: picked.toISOString() })
            : await movePost({ accountId, postUuid: post.uuid, scheduledAt: picked.toISOString() });
        if (!isSuccessfulResult(result)) {
          return false;
        }
      }
      if (!asDraft && post.status === 'draft') {
        return isSuccessfulResult(await schedulePost({ accountId, postUuid: post.uuid }));
      }
      return true;
    },
    [accountId, draft, movePost, patchPost, post, schedulePost]
  );

  return {
    isEditing: postUuid !== null,
    isLoading: postUuid !== null && isLoading,
    post,
    save,
    isSaving: patchState.isLoading || moveState.isLoading || scheduleState.isLoading,
    error: patchState.error ?? moveState.error ?? scheduleState.error,
  };
}
